import type { JSX } from "react";
import parse from "html-react-parser";
import { MinusIcon, PlusIcon } from "vega-icons";

import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { HtmlContent } from "../HtmlContent/HtmlContent";

type Props = {
  id?: string;
  body: JSX.Element | string;
  highlight?: boolean;
  index: number;
  open?: number;
  setOpen: (value: number | undefined) => void;
  title: string;
  maxHeight?: string;
};

export function FAQItemFull({
  body,
  highlight,
  id,
  index,
  open,
  setOpen,
  title,
  maxHeight,
}: Props) {
  const isOpen = open === index;

  const faqStyle = `
    <style>
      .content-container {
        transition: max-height 0.5s ease, opacity 0.5s ease;
        overflow: hidden;
        max-height: 0;
        opacity: 0;
      }
      .content-container.open {
        max-height: ${maxHeight ?? "500px"}; /* Adjust as necessary */
        opacity: 1;
      }
    </style>
  `;

  return (
    <div
      className={cnTwMerge(
        "w-full cursor-pointer faq transition-colors duration-300",
        {
          "bg-background/brand/opacity rounded-default": highlight,
        },
      )}
      id={id}
    >
      <div className="border-b-1 p-4 border-border/primary">
        <div
          className="flex items-center justify-between"
          onClick={() => setOpen(!isOpen ? index : undefined)}
        >
          <div className="select-none body-base-regular">{title}</div>
          <div className="text-sm relative">
            <div
              className={cnTwMerge(
                "w-[21px] h-[21px] flex items-center justify-center transition-opacity duration-500",
                {
                  hidden: !isOpen,
                  "opacity-100": isOpen,
                },
              )}
            >
              <MinusIcon className="w-[16px] h-[16px] border-brand-default border-1 text-brand-default rounded-full" />
            </div>
            <div
              className={cnTwMerge(
                "w-[21px] h-[21px] flex items-center justify-center transition-opacity duration-500",
                {
                  hidden: isOpen,
                  "opacity-100": !isOpen,
                },
              )}
            >
              <PlusIcon className="w-[16px] h-[16px] border-brand-default border-1 text-brand-default rounded-full" />
            </div>
          </div>
        </div>
        <div
          className={cnTwMerge(
            "content-container body-base-regular text-text/secondary transition-all duration-500 overflow-hidden",
            {
              open: isOpen,
            },
          )}
        >
          <div className="pt-2">
            <HtmlContent content={parse(`${faqStyle} ${body}`)} />
          </div>
        </div>
      </div>
    </div>
  );
}
